import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import ChargePointHistoryImport from "./components/ChargePointHistoryImport";
import ChargingStationDashboard from "./components/ChargingStationDashboard";
import SyncChargePoints from "./components/SyncChargePoints";

/** Where a never-computed period is filled in: Station Performance's backfill, which also writes fleet days. */
export const RECOMPUTE_ROUTE = "/analytics/station-performance?tab=backfill";

/**
 * BOF-76 — Analytics → Charging Stations: the fleet dashboard and the KPI scorecard that replace the
 * hand-maintained *cocharge - Charging Station Dashboard* workbook.
 *
 * **Upload workbook** imports the workbook's hand-kept monthly charge-point history — the months before
 * charge points were counted nightly. After an import the dashboard reloads, so the figures just
 * uploaded are on screen without leaving the page.
 *
 * **Sync charge points** reads every charge point's power type and status from station-ops. Nightly
 * counts and the AC/DC split need it to have run once; it is also the recovery path if clearing's copy
 * ever drifts.
 */
const ChargingStationsPage: React.FC = () => {
  const navigate = useNavigate();
  const [uploadOpen, setUploadOpen] = useState(false);
  // Remounting reloads every figure on the dashboard after an import.
  const [dashboardKey, setDashboardKey] = useState(0);
  const openRecompute = () => navigate(RECOMPUTE_ROUTE);

  return (
    <div className="layout-main">
      <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
        <div>
          <h2 className="m-0">Charging stations</h2>
          <span className="text-color-secondary text-sm">
            The fleet's charge points, energy and sessions split AC / DC, their growth, and the monthly KPI scorecard.
          </span>
        </div>
        <div className="flex flex-wrap align-items-center justify-content-end gap-2">
          <SyncChargePoints onRecompute={openRecompute} />
          <Button label="Upload workbook" icon="pi pi-upload" onClick={() => setUploadOpen(true)} />
        </div>
      </div>

      <ChargingStationDashboard key={dashboardKey} onRecompute={openRecompute} />

      <Dialog
        header="Upload the Charging Station Dashboard workbook"
        visible={uploadOpen}
        onHide={() => setUploadOpen(false)}
        style={{ width: "min(960px, 95vw)" }}
        dismissableMask
      >
        <ChargePointHistoryImport onImported={() => setDashboardKey((k) => k + 1)} onRecompute={openRecompute} />
      </Dialog>
    </div>
  );
};

export default ChargingStationsPage;
